import CloseBig from 'assets/icons/CloseBig';
import Image from 'components/Image';
import PropTypes from 'prop-types';
import React, { useCallback, useRef } from 'react';
import { above } from 'utils/mediaqueries';
import colors from 'config/theme/colors';
import styled from 'libs/styled';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useClientHeight from 'hooks/useClientHeight';
import useFocusTrap from 'hooks/useFocusTrap';
import useHeaderHeights from 'hooks/useHeaderHeights';
import zIndex from 'config/theme/z-index';

// Same position and size as BasketOverlay, so that the two overlays behave identically
const TransitionElement = styled('div', {
    shouldForwardProp: prop => ['clientHeight', 'headerHeights'].indexOf(prop) === -1,
})`
    position: absolute;
    top: ${({ headerHeights }) => headerHeights.mobile}px;
    height: ${({ clientHeight, headerHeights }) => `calc(${clientHeight} - ${headerHeights.mobile}px)`};
    width: 100vw;

    ${above.md} {
        position: fixed;
        right: 0;
        top: ${({ headerHeights }) => headerHeights.desktop}px;
        width: 40vw;
        max-width: 960px;
        height: ${({ clientHeight, headerHeights }) => `calc(${clientHeight} - ${headerHeights.desktop}px)`};
    }
`;

// The full height together with the background covers the area that the image does not fill
const Wrapper = styled('div')`
    position: relative;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    background-color: ${colors.background};

    &:focus {
        outline: none;
    }
`;

const Icon = styled('button')`
    position: absolute;
    top: 16px;
    right: 12px;

    ${above.md} {
        top: 24px;
        right: 24px;
    }

    &:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }
`;

const HarmonisationOverlay = ({ returnFocusId }) => {
    const { t } = useTranslation();
    const overlay = useSelector(state => state.overlay);
    const harmonisationImage = useSelector(state => state.header.data.basket?.harmonisationImage) || {};
    const headerHeights = useHeaderHeights();
    const clientHeight = useClientHeight();
    const panelRef = useRef(null);
    const isActive = overlay.current === 'harmonisationOverlay';

    // The overlay state only holds one overlay at a time, so re-open the basket the user came from
    const closeOverlay = useCallback(() => {
        overlay.show('basketOverlay', {
            transparent: false,
            zIndex: zIndex.basket,
            isUpsell: false,
            returnFocusId,
        });
    }, [overlay, returnFocusId]);

    useFocusTrap(panelRef, isActive, closeOverlay);

    return (
        <TransitionElement className="slide-in right" clientHeight={clientHeight} headerHeights={headerHeights}>
            <Wrapper
                ref={panelRef}
                aria-label={t('checkout_basket.harmonisation')}
                aria-modal="true"
                role="dialog"
                tabIndex={-1}
            >
                {harmonisationImage.url && (
                    <Image
                        alt={harmonisationImage.alt || harmonisationImage.title || ''}
                        display="block"
                        height="auto"
                        imageBackground={colors.background}
                        loading="eager"
                        sizes={['100vw', null, 'min(40vw, 960px)']}
                        src={{ url: harmonisationImage.url, width: [480, 768, 960, 1440, 1920] }}
                        width="100%"
                    />
                )}
                <Icon aria-label={t('checkout_basket.close')} type="button" onClick={closeOverlay}>
                    <CloseBig aria-hidden="true" />
                </Icon>
            </Wrapper>
        </TransitionElement>
    );
};

HarmonisationOverlay.propTypes = {
    returnFocusId: PropTypes.string,
};

export default HarmonisationOverlay;
